import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { BACKEND_URL } from '@/lib/api';
import { Briefcase, MapPin, Clock, Loader2, CheckCircle2, AlertTriangle, Paperclip } from 'lucide-react';

const publicApi = axios.create({ baseURL: `${BACKEND_URL}/api`, timeout: 20000 });

const OG_IMAGE = '/og-werken-bij.png';

const TYPE_LABEL = { vast: 'Vast dienstverband', tijdelijk: 'Tijdelijk', oproep: 'Oproepkracht' };

export default function PublicVacature() {
  const { id } = useParams();
  const [vacature, setVacature] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ name: '', email: '', telefoon: '', woonplaats: '', motivatie: '' });
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await publicApi.get(`/public/vacatures/${id}`);
      setVacature(data);
    } catch (e) {
      if (e.response?.status === 404) setNotFound(true);
      else setError('Kon de vacature niet laden. Probeer het later opnieuw.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    document.head.querySelectorAll('[data-default-seo]').forEach((el) => el.remove());
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('name', form.name);
      fd.append('email', form.email);
      if (form.telefoon) fd.append('telefoon', form.telefoon);
      if (form.woonplaats) fd.append('woonplaats', form.woonplaats);
      if (form.motivatie) fd.append('motivatie', form.motivatie);
      const file = fileRef.current?.files?.[0];
      if (file) fd.append('cv', file);
      await publicApi.post(`/public/vacatures/${id}/apply`, fd);
      setDone(true);
    } catch (err) {
      const s = err.response?.status;
      if (s === 404) { setNotFound(true); }
      else if (s === 413) setError('Je CV is te groot (max 10 MB). Kies een kleiner bestand.');
      else if (s === 400) setError('Dit bestandstype wordt niet ondersteund. Gebruik PDF, Word, JPG of PNG.');
      else if (s === 422) setError(err.response?.data?.detail || 'Controleer je gegevens en probeer opnieuw.');
      else if (s === 429) setError('Te veel sollicitaties. Probeer het later opnieuw.');
      else setError('Verzenden mislukt. Probeer het later opnieuw.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-tw1-brand-navy" data-testid="public-vacature-page">
      {(() => {
        const loc = vacature?.location_name || vacature?.plaats;
        const pageTitle = vacature
          ? `${vacature.title}${loc ? ` — ${loc}` : ''} | Werken bij Truckwash1`
          : 'Vacature | Werken bij Truckwash1';
        const rawDesc = vacature?.description || vacature?.functie
          || 'Solliciteer direct op deze vacature bij Truckwash1.';
        const desc = String(rawDesc).replace(/\s+/g, ' ').trim().slice(0, 200);
        const origin = typeof window !== 'undefined' ? window.location.origin : '';
        const url = origin ? origin + window.location.pathname : '';
        const ogImageUrl = origin + OG_IMAGE;
        return (
          <Helmet>
            <title>{pageTitle}</title>
            <meta name="description" content={desc} />
            {url && <link rel="canonical" href={url} />}
            <meta property="og:type" content="article" />
            <meta property="og:site_name" content="Truckwash1" />
            <meta property="og:title" content={pageTitle} />
            <meta property="og:description" content={desc} />
            {url && <meta property="og:url" content={url} />}
            <meta property="og:image" content={ogImageUrl} />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={pageTitle} />
            <meta name="twitter:description" content={desc} />
            <meta name="twitter:image" content={ogImageUrl} />
          </Helmet>
        );
      })()}
      <header className="border-b border-white/10 px-6 py-4">
        <div className="mx-auto flex max-w-3xl items-center gap-3">
          <img src="/images/logo.png" alt="Truckwash1" className="h-8 w-auto object-contain" />
          <div className="h-6 w-px bg-white/20" />
          <span className="text-lg font-bold tracking-tight text-white">Werken bij Truckwash1</span>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-10">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-24 text-white/70">
            <Loader2 size={18} className="animate-spin" /> Laden…
          </div>
        ) : notFound ? (
          <div className="rounded-2xl bg-white p-10 text-center shadow-xl" data-testid="public-vacature-unavailable">
            <AlertTriangle size={40} className="mx-auto mb-3 text-amber-500" />
            <h1 className="text-2xl font-bold text-tw1-brand-navy">Vacature niet (meer) beschikbaar</h1>
            <p className="mt-2 text-sm text-slate-600">Deze vacature bestaat niet of is gesloten. Bekijk onze andere openstaande vacatures.</p>
          </div>
        ) : done ? (
          <div className="rounded-2xl bg-white p-10 text-center shadow-xl" data-testid="public-apply-success">
            <CheckCircle2 size={44} className="mx-auto mb-3 text-emerald-500" />
            <h1 className="text-2xl font-bold text-tw1-brand-navy">Bedankt voor je sollicitatie!</h1>
            <p className="mt-2 text-sm text-slate-600">We hebben je sollicitatie ontvangen en nemen zo snel mogelijk contact met je op.</p>
          </div>
        ) : vacature ? (
          <div className="space-y-6">
            <div className="rounded-2xl bg-white p-8 shadow-xl">
              <div className="flex items-center gap-2 text-tw1-accent-gold">
                <Briefcase size={18} />
                <span className="text-xs font-bold uppercase tracking-wider">Vacature</span>
              </div>
              <h1 className="mt-2 text-3xl font-bold text-tw1-brand-navy" data-testid="public-vacature-title">{vacature.title}</h1>
              <div className="mt-3 flex flex-wrap gap-4 text-sm text-slate-600">
                {vacature.plaats && <span className="inline-flex items-center gap-1"><MapPin size={14} /> {vacature.plaats}</span>}
                {vacature.employment_type && <span className="inline-flex items-center gap-1"><Briefcase size={14} /> {TYPE_LABEL[vacature.employment_type] || vacature.employment_type}</span>}
                {vacature.hours != null && <span className="inline-flex items-center gap-1"><Clock size={14} /> {vacature.hours} uur/week</span>}
              </div>
              {vacature.functie && <p className="mt-3 text-sm font-medium text-slate-700">Functie: {vacature.functie}</p>}
              {vacature.description && (
                <p className="mt-4 whitespace-pre-line text-sm leading-relaxed text-slate-700" data-testid="public-vacature-description">
                  {vacature.description}
                </p>
              )}
            </div>

            <form onSubmit={handleSubmit} className="rounded-2xl bg-white p-8 shadow-xl space-y-4" data-testid="public-apply-form">
              <h2 className="text-xl font-bold text-tw1-brand-navy">Solliciteer direct</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                <PField label="Naam" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="apply-name" />
                <PField label="E-mailadres" type="email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="apply-email" />
                <PField label="Telefoon" value={form.telefoon} onChange={(v) => setForm({ ...form, telefoon: v })} testid="apply-telefoon" />
                <PField label="Woonplaats" value={form.woonplaats} onChange={(v) => setForm({ ...form, woonplaats: v })} testid="apply-woonplaats" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">Motivatie</label>
                <textarea rows={4} value={form.motivatie} onChange={(e) => setForm({ ...form, motivatie: e.target.value })} data-testid="apply-motivatie"
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" placeholder="Vertel kort waarom jij geschikt bent…" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">CV (optioneel — PDF, Word, JPG of PNG, max 10 MB)</label>
                <div className="flex items-center gap-2 rounded-md border border-dashed border-slate-300 px-3 py-2">
                  <Paperclip size={15} className="text-slate-400" />
                  <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" data-testid="apply-cv" className="text-sm" />
                </div>
              </div>

              {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="apply-error">{error}</div>}

              <button type="submit" disabled={submitting} data-testid="btn-apply-submit"
                className="flex w-full items-center justify-center gap-2 rounded-md bg-tw1-brand-navy px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-navy-light disabled:opacity-60">
                {submitting ? <><Loader2 size={15} className="animate-spin" /> Verzenden…</> : 'Verstuur sollicitatie'}
              </button>
            </form>
          </div>
        ) : null}
      </main>
    </div>
  );
}

function PField({ label, value, onChange, type = 'text', required, testid }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-semibold text-slate-600">{label}{required && ' *'}</label>
      <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
    </div>
  );
}
